'use client';

import { useState, useEffect } from 'react';
import { Download, X, Share, Smartphone, Monitor, MoreVertical, PlusSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export default function InstallButton() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMounted(true);
      // Check if already installed
      if (window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true) {
        setIsStandalone(true);
      }
    }, 0);

    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setIsStandalone(true);
      }
    } else {
      // Show manual instructions if prompt is not available (e.g., iOS, iframe, or already dismissed)
      setShowInstructions(true);
    }
  };

  // Don't render anything until mounted to avoid hydration mismatch
  // Also hide if already installed
  if (!mounted || isStandalone) return null;

  return (
    <div className="relative">
      <button
        onClick={handleInstallClick}
        className="flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 px-3 sm:px-4 py-2 text-white hover:bg-white/20 transition-all active:scale-95 group shadow-lg"
        title="Instalar Aplicativo"
      >
        <Download className="w-4 h-4 sm:w-5 sm:h-5 text-blue-200 group-hover:text-white transition-colors" />
        <span className="font-black text-[10px] sm:text-xs uppercase tracking-tighter italic">Instalar App</span>
      </button>

      <AnimatePresence>
        {showInstructions && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white text-slate-800 p-5 sm:p-6 rounded-3xl shadow-2xl border border-slate-100 w-full max-w-md max-h-[90vh] flex flex-col"
            >
              <div className="flex justify-between items-start mb-4 flex-shrink-0">
                <h4 className="font-black uppercase tracking-tighter italic text-base text-blue-600">Como Instalar o App</h4>
                <button 
                  onClick={() => setShowInstructions(false)} 
                  className="text-slate-400 hover:text-slate-600 bg-slate-100 rounded-full p-1.5 transition-colors"
                  title="Fechar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4 overflow-y-auto pr-2 custom-scrollbar">
                {/* iOS Section */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                  <h5 className="font-bold text-slate-800 text-sm uppercase mb-3 flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-slate-500" /> No iPhone/iPad (Safari)
                  </h5>
                  <ol className="text-sm text-slate-600 space-y-3 list-decimal list-inside ml-1">
                    <li>Toque no botão <strong>Compartilhar</strong> <Share className="w-4 h-4 inline text-blue-500 mx-0.5" /> na barra inferior.</li>
                    <li>Role a lista de opções para baixo.</li>
                    <li>Toque em <strong>&quot;Adicionar à Tela de Início&quot;</strong> <PlusSquare className="w-4 h-4 inline text-slate-500 mx-0.5" />.</li>
                  </ol>
                </div>

                {/* Android Section */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                  <h5 className="font-bold text-slate-800 text-sm uppercase mb-3 flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-slate-500" /> No Android (Chrome)
                  </h5>
                  <ol className="text-sm text-slate-600 space-y-3 list-decimal list-inside ml-1">
                    <li>Toque no <strong>Menu</strong> <MoreVertical className="w-4 h-4 inline text-slate-500 mx-0.5" /> no canto superior direito.</li>
                    <li>Selecione <strong>&quot;Adicionar à Tela Inicial&quot;</strong> ou <strong>&quot;Instalar aplicativo&quot;</strong>.</li>
                  </ol>
                </div>
                
                {/* Desktop Section */}
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                  <h5 className="font-bold text-slate-800 text-sm uppercase mb-3 flex items-center gap-2">
                    <Monitor className="w-5 h-5 text-slate-500" /> No Computador
                  </h5>
                  <p className="text-sm text-slate-600 ml-1 leading-relaxed">
                    Clique no ícone de instalação <Download className="w-4 h-4 inline text-slate-500 mx-0.5" /> localizado no lado direito da barra de endereços do seu navegador.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
